import React from 'react';

/**
 * Minimal, dependency-free markdown renderer for chat messages.
 * Supports: **bold**, `inline code`, # headings, bullet and numbered lists,
 * and paragraph breaks. Builds React elements directly — no innerHTML.
 */

const renderInline = (text, keyPrefix) => {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  let i = 0;
  const str = String(text || '');
  while ((m = regex.exec(str))) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-b${i++}`} className="font-semibold text-[#0f172a]">{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={`${keyPrefix}-c${i++}`} className="px-1 py-0.5 rounded bg-[#F2F2F7] text-[12px] font-mono text-[#3A3A3C]">
          {tok.slice(1, -1)}
        </code>
      );
    }
    last = m.index + tok.length;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts;
};

const MarkdownLite = ({ text }) => {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let list = null; // { ordered: bool, items: [] }

  const flushList = () => {
    if (!list) return;
    const key = `list-${blocks.length}`;
    const items = list.items.map((item, i) => (
      <li key={`${key}-i${i}`} className="mb-1">{renderInline(item, `${key}-i${i}`)}</li>
    ));
    blocks.push(
      list.ordered
        ? <ol key={key} className="list-decimal pl-5 my-1.5">{items}</ol>
        : <ul key={key} className="list-disc pl-5 my-1.5">{items}</ul>
    );
    list = null;
  };

  lines.forEach((line, idx) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const heading = line.match(/^\s*#{1,3}\s+(.*)$/);

    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      const content = (bullet || numbered)[1];
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(content);
      return;
    }

    flushList();

    if (heading) {
      blocks.push(
        <p key={`h-${idx}`} className="font-bold text-[#0f172a] mt-2 mb-1">{renderInline(heading[1], `h-${idx}`)}</p>
      );
    } else if (line.trim() === '') {
      // paragraph gap is handled by block margins; skip empty lines
    } else {
      blocks.push(
        <p key={`p-${idx}`} className="mb-1.5 last:mb-0">{renderInline(line, `p-${idx}`)}</p>
      );
    }
  });

  flushList();

  return <div className="text-left">{blocks}</div>;
};

export default MarkdownLite;
